import { Form } from '@epages/react-components'
import type { TFunction } from 'i18next'
import { fromJS } from 'immutable'
import { useRef } from 'react'

import { extractGoogleMapsEmbedded } from './extractGoogleMapsSource'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import FormGroup from '../../../../formComponents/FormGroup'
import InputField from '../../../../formComponents/InputField'

function normalize(data: ImmutableMap) {
  return data.update('iframe', (iframe) => (iframe ? iframe.trim() : ''))
}

function validate(tScoped: TFunction, data: ImmutableMap, isOnSubmit = true) {
  const iframe = data.get('iframe')
  const src = extractGoogleMapsEmbedded(iframe)
  return (
    isOnSubmit &&
    fromJS({
      iframe:
        // when the input is not empty but invalid show an error
        Boolean(iframe) && !src ? tScoped('urlInputField.validationMessages.invalidURL') : null,
    })
  )
}

export default function GoogleMapsSettingsForm({
  data,
  onSave,
  onCancel,
  renderLayout,
  onDataChange,
}: Readonly<WorkspacePluginSettingsProps>) {
  const t = useTranslate('interface', 'components.gmapsElementComponent.settingsModal') as TFunction

  const onValidate = (data: ImmutableMap, isOnSubmit: boolean) => validate(t, data, isOnSubmit)

  const googleMapsPbRef = useRef<string>(undefined)

  const onChange = (data) => {
    const googleMapsPb = extractGoogleMapsEmbedded(data.get('iframe'))

    // Trigger editor preview if the pb parameter changed
    if (googleMapsPb && googleMapsPb !== googleMapsPbRef.current) {
      googleMapsPbRef.current = googleMapsPb
      onDataChange(data)
    }
  }

  const title = data.get('iframe') ? t('title.onEdit') : t('title.onCreate')

  const body = (
    <FormGroup name="iframe" tagName="label" label={t('embedded.label')} info={t('embedded.info')}>
      <InputField autoFocus name="iframe" type="text" />
    </FormGroup>
  )

  const footer = (
    <ButtonRow>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('cancelButton.label')}
      </Button>
      <Button type="submit">{t('saveButton.label')}</Button>
    </ButtonRow>
  )

  return (
    <Form
      name="pluginGoogleMapsSettings"
      value={data}
      onChange={onChange}
      onSubmit={onSave}
      validate={onValidate}
      normalize={normalize}
    >
      {renderLayout({ title, body, footer })}
    </Form>
  )
}
